import { useMediaQuery, useTheme } from '@mui/material';
import { Configuration, SuccessResponse_1OfGetConfigurationResponse } from '../../../../app/services/api/generated';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import { DrawerNavItem } from '../interfaces/DrawerNavItem';
import {
  UpdateOutlined, ManageAccountsOutlined, FastfoodOutlined, KitchenOutlined, LocalShippingOutlined,
  ScaleOutlined, MenuBookOutlined, SwapCallsOutlined,
  Checklist,
  CalendarViewWeek,
  Flaky,
  ChecklistRtl,
  Rule,
  LineStyle,
  AssignmentTurnedIn,
  FormatListBulleted,
  FolderOutlined,
} from '@mui/icons-material';
import {
  ListIcon, Building05, PackageCheck,
  ShoppingCart01, CoinsSwap02, CoinsHand, BarChart08,
  Truck02, MarkerPin01, ReceiptCheck, FileCheck01, Trash03, Cube,
  Settings01,
} from '../../../../assets';
import { useNavigate } from 'react-router-dom';
import useLogError from '../../../../hooks/useLogError';
import { System } from '../../../../enums/System';

const appNavItems: DrawerNavItem[] = [
  /*  {
      canRender: false,
      translationKey: 'home',
      icon: <Home02 />,
      href: '/',
      systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
    }, */
  {
    canRender: false,
    translationKey: 'catalogues',
    icon: <ListIcon />,
    href: '/catalogue-list',
    permissions: ['CataloguesView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'retailers',
    icon: <Building05 />,
    href: '/retailers',
    permissions: ['RetailersView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'orderList',
    icon: <ShoppingCart01 />,
    href: '/orders',
    permissions: ['OrdersView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'deliveries',
    icon: <Truck02 />,
    href: '/deliveries',
    permissions: ['DeliveriesView'],
    systemIds: [System.OrderlyForDistributors],
  }/*,{
    canRender: true,
    translationKey: 'claims',
    icon: <FileCheck01 />,
    href: '/claims',
  }*/, {
    canRender: false,
    translationKey: 'finance',
    icon: <CoinsHand />,
    href: '/finance',
    permissions: ['FinanceView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'reporting',
    icon: <BarChart08 />,
    href: '/reports',
    permissions: ['ReportsView'],
    systemIds: [System.OrderlyForDistributors],
  },
  {
    canRender: false,
    translationKey: 'orderStock',
    icon: <ShoppingCart01 />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'browseProducts',
        icon: <ListIcon />,
        href: '/store/orderable-items',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'historicalOrders',
        icon: <LocalShippingOutlined />,
        href: '/store/orders-history',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'orderViaForm',
        icon: <UpdateOutlined />,
        href: '/store/order-planning',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'receiveStock',
    icon: <LocalShippingOutlined />,
    permissions: ['PurchaseOrderRead'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'purchaseOrder',
        icon: <ReceiptCheck />,
        href: '/store/goods-receipts',
        permissions: ['PurchaseOrderRead'],
        systemIds: [System.OrderlyForStores],
      }, {
        canRender: false,
        translationKey: 'invoices',
        icon: <FileCheck01 />,
        href: '/invoices',
        permissions: ['InvoiceReconciliationRead', 'InvoiceReconciliationWrite', 'InvoiceReconciliationAdmin'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'transferStock',
    icon: <SwapCallsOutlined />,
    permissions: ['TransferRead'],
    href: '/store/stock-transfers',
    systemIds: [System.OrderlyForStores],
  },
  {
    canRender: false,
    translationKey: 'wasteStock',
    icon: <Trash03 />,
    href: '/store/waste-logs',
    permissions: ['DailyWasteCheckSheetRead'],
    systemIds: [System.OrderlyForStores],
  },
  {
    canRender: false,
    translationKey: 'count',
    icon: <Cube />,
    permissions: ['StockRead', 'StockCountAdmin', 'StockCountRead', 'StockLocationRead'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'stockCount',
        icon: <PackageCheck />,
        href: '/store/stock-count',
        permissions: ['StockCountAdmin', 'StockCountRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'stockOnHand',
        icon: <CoinsHand />,
        permissions: ['StockRead'],
        href: '/store/stock',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'countZones',
        icon: <MarkerPin01 />,
        href: '/store/stock-count-zones',
        permissions: ['StockLocationRead'],
        storePermission: 'LocationsEnabled',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'transactions',
    icon: <CoinsSwap02 />,
    permissions: ['TransactionAdmin', 'TransactionRead'],
    href: '/transactions',
    systemIds: [System.OrderlyForStores],
  },
  {
    canRender: false,
    translationKey: 'cashManagement',
    icon: <CoinsHand />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'cashOverview',
        icon: <CoinsHand />,
        permissions: ['CashManagementView', 'CashManagementCreate', 'CashManagementEdit', 'TransactionRead'],
        href: '/cash-management',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'cashReconciliation',
        icon: <CoinsSwap02 />,
        permissions: ['CashReconciliationRead'],
        href: '/cash-reconciliation',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'products',
    icon: <FastfoodOutlined />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'viewItems',
        icon: <FastfoodOutlined />,
        href: '/store/products?allItems=true',
        permissions: ['ItemRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'viewRecipes',
        icon: <KitchenOutlined />,
        href: '/store/recipes',
        permissions: ['RecipeRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'uomConversions',
        icon: <ScaleOutlined />,
        href: '/uom-conversions',
        permissions: ['ConversionRead'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'tasks',
    icon: <Checklist />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'operationalDay',
        icon: <LineStyle />,
        href: '/operational-day',
        permissions: ['OperationalDaysViewDashboard'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'taskDashboard',
        icon: <AssignmentTurnedIn />,
        href: '/task-reporting',
        permissions: ['AdvancedTasksView'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'tasks',
        icon: <FormatListBulleted />,
        href: '/tasks',
        permissions: ['AdvancedTasksView'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'reporting',
    icon: <BarChart08 />,
    href: '/reports',
    permissions: ['ReportRead'],
    systemIds: [System.OrderlyForStores],
  }];

const systemNavItems: DrawerNavItem[] = [{
  canRender: true,
  translationKey: 'userSettings',
  icon: <ManageAccountsOutlined />,
  href: '/user-settings',
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
}, {
  canRender: true,
  translationKey: 'documents',
  icon: <FolderOutlined />,
  href: '/documents',
  permissions: ['DocumentsView'],
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
}, {
  canRender: true,
  translationKey: 'settings',
  icon: <Settings01 />,
  href: '/settings',
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
},
];

export const useDrawer = () => {
  const { getConfigurations } = useNswagClient();
  const theme = useTheme();
  const { selectedStore, hasPermissionTo, user, selectedSystem, systems } = useContext(UserContext);
  const [storeConfig, setStoreConfig] = useState<Configuration[]>([]);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedNavItem, setSelectedNavItem] = useState<DrawerNavItem>();
  const slidingSubMenuWidth = 320;
  const { logError } = useLogError();
  const [appNavItemsToUse, setAppNavItemsToUse] = useState<DrawerNavItem[]>([]);
  const [systemNavItemsToUse, setSystemNavItemsToUse] = useState<DrawerNavItem[]>([]);

  useEffect(() => {
    if (selectedSystem) {

      const updatedNavItems = appNavItems.map((item) => {
        checkPermissions(item, storeConfig);
        return item;
      });

      setAppNavItemsToUse(updatedNavItems);

    } else {
      setAppNavItemsToUse([]);
      console.log('wipe');
    }

    if (systems.some(x => x.id === 0)) {
      const updatedSystemNavItems = systemNavItems.map((item) => {
        checkPermissions(item, storeConfig);
        return item;
      });

      setSystemNavItemsToUse(updatedSystemNavItems);

    } else {
      setSystemNavItemsToUse([]);
    }
  }, [selectedSystem, storeConfig]);

  const handleNavItemClick = (item: DrawerNavItem) => {
    if (item.href || ((item.children?.length ?? 0) > 0)) {
      setSelectedNavItem((prev) => {
        if (!item?.children) return undefined;
        return prev?.translationKey === item.translationKey ? undefined : item;
      });

      if (!item.children) {
        navigate(item.href ?? '/');
      }
    }

  };

  useEffect(() => {
    if (selectedStore) {
      getConfigurations(selectedStore.franchiseName ?? '', selectedStore.storeNumber ?? '')
        .then((result: SuccessResponse_1OfGetConfigurationResponse) => {
          setStoreConfig(result.data?.configurations ?? []);
        })
        .catch((error) => logError(error));
    }
  }, [selectedStore, user]);

  const hasStorePermission = (per: string | undefined, config: Configuration[]): boolean => {
    if (per === undefined) return true;

    return config.some(x => x.name === per && x.value === 'true');
  };

  const checkPermissions = (item: DrawerNavItem, configurations: Configuration[]): boolean => {
    // Determine if the current item can render
    const childPermissions = item.children?.flatMap((child) => child.permissions ?? []) ?? [];
    const userCanAccessPageOrSubPage = !item.permissions || hasPermissionTo(item.permissions) || hasPermissionTo(childPermissions);
    const userCanAccessSystem = item.systemIds?.some((systemId) => systemId === selectedSystem?.id) ?? false;
    const storeCanAccessPage = !item.storePermission || hasStorePermission(item.storePermission ?? [], configurations);

    const canRender = userCanAccessPageOrSubPage && storeCanAccessPage && userCanAccessSystem;

    item.canRender = canRender;

    item.children?.forEach(i => checkPermissions(i, configurations));

    return item.canRender;
  };

  const isHighLighted = (item: DrawerNavItem, pathname: string, search: string) => {
    const urlEquals: boolean = item.children ? item.children.some(i => i?.href === pathname) : item?.href === pathname;

    const fullUrl = pathname + search;
    let urlstarts: boolean = item.children ? item.children.some(i => fullUrl.startsWith(i?.href ?? '-')) : fullUrl.startsWith(item.href ?? '-');

    if (search === '' || item.href === '/') {
      urlstarts = false;
    }

    return urlEquals || urlstarts;
  };

  return { storeConfig, open, setOpen, isMobile, isHighLighted, selectedNavItem, setSelectedNavItem, appNavItemsToUse, handleNavItemClick, systemNavItemsToUse, slidingSubMenuWidth };
};
